import {
  Component,
  ElementRef,
  inject,
  Input,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  input,
  viewChild,
  output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzImageModule } from 'ng-zorro-antd/image';
import { NzMessageService } from 'ng-zorro-antd/message';
import { FlIconComponent } from '@common';
import { COSService } from '@shared/service/cos.service';
import { MessageSummaryComponent } from '../message-summary/message-summary.component';
import { MessageType } from '../../message.api.service';

/** 附件类型：图片/视频/音频/文件 */
export type ReplyMediaKind = 'image' | 'video' | 'audio' | 'file';

/** 附件类型判断（与渲染侧 mediaType 同规则：按后缀嗅探） */
const IMAGE_EXT = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'avif'];
const VIDEO_EXT = ['mp4', 'webm', 'mov', 'ogg', 'm4v', 'avi', 'ts'];
const AUDIO_EXT = ['mp3', 'wav', 'amr', 'aac', 'm4a'];

/** 单次消息最多图片数：老师一次最多发 3 张图片 */
const MAX_IMAGE_COUNT = 3;

/** 图片最大边长（像素）：超大图学生端查看会崩溃（已复现），超限拒绝上传 */
const MAX_IMAGE_PIXEL = 5000;

function kindOf(name: string): ReplyMediaKind {
  const clean = String(name).split('?')[0];
  const lastDot = clean.lastIndexOf('.');
  const suffix = lastDot >= 0 ? clean.slice(lastDot + 1).toLowerCase() : '';
  if (IMAGE_EXT.includes(suffix)) return 'image';
  if (VIDEO_EXT.includes(suffix)) return 'video';
  if (AUDIO_EXT.includes(suffix)) return 'audio';
  return 'file';
}

/** 引用条配置 */
export interface ReplyQuote {
  /** 有具体回复对象时的引导词（如「回复」） */
  label?: string;
  /** 无具体对象时的文案（如「回复全班」） */
  fallback?: string;
}

/** 上传配置：scope + 拿令牌所需参数（不同宿主可配不同 COS 目录） */
export interface ReplyUpload {
  /** COS scope，如 'blackboard'（板书目录） */
  scope: string;
  /** 令牌参数，如 { taskId } */
  extra?: Record<string, unknown>;
}

/** 回复目标：msgId 有 → 回复具体对象（带引用）；无 → 回复全部 */
export interface ReplyTarget {
  /** 目标消息 id（点学生头像时携带，宿主发送时用） */
  msgId?: number;
  /** 被回复对象姓名（引用条显示） */
  firstName?: string;
  /** 被回复内容摘要（引用条显示） */
  content?: string;
  /** 被回复消息的类型（1 不懂 2 我来讲；0 默认不展示） */
  messageType?: MessageType;
  /** 被回复消息的标签（学生只发标签时正文为空，摘要靠标签展示） */
  tags?: { firstName?: string; tag: string }[];
  /** 被回复消息的资源 URL（图片/语音等），摘要里按类型显示 [图片]/[语音] */
  mediaUrls?: string[];
  /** 头像地址（小头像） */
  avatar?: string;
}

/** 回复器已选附件（上传完成后待随内容一起发送） */
export interface ReplyAttachment {
  /** 可访问 URL（cos.toUrl 处理后的完整地址） */
  url: string;
  /** 原始文件名 */
  name: string;
  /** 附件类型：图片/视频/音频/文件 */
  kind: ReplyMediaKind;
}

/** 发送载荷：内容 + 附件 URL（「同步全部」等宿主自身状态由宿主在 send 回调里自行合并） */
export interface ReplySendPayload {
  content: string;
  mediaUrls: string[];
}

/**
 * 底部回复器（基础组件，与任务/消息宿主解耦）
 *
 * 内聚「多行输入 + 图片/附件上传 + 发送」的稳定内核：
 * - 顶部引用条由 quote 输入控制（对象=显示，可配文案；false/null=不显示）
 * - 「同步全部」等额外操作区交给宿主通过 `[toAll]` 插槽注入，组件不做任何勾选处理
 * - 上传 scope/参数通过 upload 输入配置；false=禁用上传
 * 仅收集输入并抛 send / cancel 事件，发送（含 toAll）由宿主负责。
 */
@Component({
  selector: 'fl-reply-composer',
  templateUrl: './reply-composer.component.html',
  styleUrls: ['./reply-composer.component.scss'],
  standalone: true,
  providers: [COSService],
  imports: [FormsModule, NzIconModule, NzImageModule, FlIconComponent, MessageSummaryComponent],
})
export class ReplyComposerComponent implements OnChanges {
  /** 回复目标；非 null 时才渲染整个回复器 */
  // TODO: Skipped for migration because:
  //  This input is used in a control flow expression (e.g. `@if` or `*ngIf`)
  //  and migrating would break narrowing currently.
  @Input() replyTarget: ReplyTarget | null = null;

  /** 引用条配置；false/null 不显示引用条 */
  // TODO: Skipped for migration because:
  //  This input is used in a control flow expression (e.g. `@if` or `*ngIf`)
  //  and migrating would break narrowing currently.
  @Input() quote: ReplyQuote | false = {};

  /** 上传配置；false 禁用上传，对象则按 scope+extra 上传 */
  readonly upload = input<ReplyUpload | false>({ scope: 'blackboard' });

  /** 占位文案；空串时按回复目标自动派生（给个人「发消息给{姓名}」/给全班「点击消息回复个人或在此处发送消息给全班」） */
  readonly placeholder = input('');

  /** 清空信号：宿主切题/发送/关闭时递增，触发清空输入，避免依赖 replyTarget 是否真的变化 */
  readonly clearTick = input(0);

  /** 点击发送（携带内容 + 附件 URL） */
  readonly send = output<ReplySendPayload>();

  /** 关闭回复 */
  readonly cancel = output<void>();

  /** 输入文本 */
  content = '';

  /** 内容是否已折行（视觉 wrap，不产生 \n）。折行后单行布局切回多行，避免文字被挤压/出滚动条 */
  isWrapped = false;

  /** 输入框是否聚焦：聚焦即展开为多行形态，让老师输入前就看到底部工具栏里的「全班可见」 */
  isFocused = false;

  /** 输入框引用（自适应高度用） */
  private readonly composerInput = viewChild<ElementRef<HTMLTextAreaElement>>('composerInput');

  /** 已选附件（上传完成后展示，随内容一起发送） */
  attachments: ReplyAttachment[] = [];

  /** 上传中 */
  uploading = false;

  private cos = inject(COSService);
  private messageSrv = inject(NzMessageService);
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  /** 是否有具体回复对象（引用条 label/fallback 分支依据） */
  get isTargetStudent(): boolean {
    return !!this.replyTarget?.msgId;
  }

  /** 是否可发送（有内容或附件、且非上传中）——控制发送按钮可点态与配色 */
  get canSend(): boolean {
    return !this.uploading && (!!this.content.trim() || this.attachments.length > 0);
  }

  /**
   * 是否采用「单行极简同排」布局：给全班 + 单行文字（无换行符、未折行）+ 无附件 **+ 未聚焦**。
   * 聚焦后即展开多行形态，避免老师直接在单行态输入时不知道这条发的是全班。
   */
  get isSingleRow(): boolean {
    return !this.isTargetStudent && !this.isWrapped && this.attachments.length === 0 && !this.isFocused;
  }

  /** 占位文案：显式 platform 优先，否则按回复目标派生 */
  get displayPlaceholder(): string {
    const placeholder = this.placeholder();
    if (placeholder) return placeholder;
    // 回复个人：提示去向
    if (this.isTargetStudent) return `发消息给${this.replyTarget?.firstName || '该学生'}`;
    // 回复全班：提示操作
    return '点击消息回复个人或在此处发送消息给全班';
  }

  ngOnChanges(changes: SimpleChanges) {
    // 清空信号变化（切题/发送/关闭）或切换回复目标（头像 A→B）时清空上轮输入与附件，并重置输入框高度：
    // 常驻「回复全班」场景 replyTarget 本就不变，需靠 clearTick 驱动；切换具体回复对象则靠 replyTarget 变化
    if (changes['clearTick'] || changes['replyTarget']) {
      this.content = '';
      this.attachments = [];
      // 一并复位聚焦态回到单行极简形态（失焦时的收回见 onFocusOut）
      this.isFocused = false;
      requestAnimationFrame(() => {
        this.autoGrow();
        // OnPush：rAF 回调非模板事件，折行态（isWrapped）影响单行/多行布局需主动标记
        this.cdr.markForCheck();
      });
    }
  }

  /**
   * 焦点离开回复器：无内容且无附件时收回单行极简形态。
   * 焦点只在回复器内部移动（点图片按钮 / 勾选框）时不收回 —— 否则布局跳变会让这一次点击落空。
   */
  onFocusOut(event: FocusEvent) {
    const next = event.relatedTarget as Node | null;
    if (next && (event.currentTarget as HTMLElement).contains(next)) return;
    if (!this.content.trim() && this.attachments.length === 0) {
      this.isFocused = false;
    }
  }

  /** 选择并上传图片 */
  onPickImage(files: FileList | null, input: HTMLInputElement) {
    this.pickAndUpload(files);
    // 重置 input.value，使同一文件可再次选择触发 change
    input.value = '';
  }

  /** 选择并上传附件 */
  onPickAttachment(files: FileList | null, input: HTMLInputElement) {
    this.pickAndUpload(files);
    input.value = '';
  }

  /** 粘贴图片：从剪贴板取图片文件并上传；非图片内容（文字等）放行默认行为，不拦截 */
  onPaste(event: ClipboardEvent) {
    const items = event.clipboardData?.items;
    if (!items) return;
    const imageFiles: File[] = [];
    for (const item of Array.from(items)) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) imageFiles.push(file);
      }
    }
    if (imageFiles.length === 0) return;
    // 上传禁用或上传中时不可接收图片，放行默认粘贴行为（与点📷在该状态下不可点一致），避免静默吞图
    if (this.upload() === false || this.uploading) return;
    // 确有图片需要上传，才阻止默认粘贴，避免破坏正常文字粘贴
    event.preventDefault();
    this.pickAndUpload(imageFiles);
  }

  /** 读取图片像素尺寸；非图片或解码失败返回 null（失败时不拦截，保持原有行为） */
  private async getImageSize(file: File): Promise<{ width: number; height: number } | null> {
    try {
      const bitmap = await createImageBitmap(file);
      const size = { width: bitmap.width, height: bitmap.height };
      bitmap.close();
      return size;
    } catch {
      return null;
    }
  }

  /** 选文件 → COS 上传 → 加入附件列表（图片限 3 张、单张 ≤5000×5000 像素） */
  private async pickAndUpload(files: FileList | File[] | null) {
    const upload = this.upload();
    if (upload === false || !files?.length || this.uploading) return;
    const scope = upload.scope;
    const extra = upload.extra;
    if (!extra) {
      this.messageSrv.error('缺少上传上下文，无法上传');
      return;
    }
    this.cos.setup(scope as never, extra as never);
    // 一次选/粘贴视为一批，整批期间置为上传中：禁用按钮且拦截重入（消除尺寸校验 await 期间的并发窗口）
    this.uploading = true;
    try {
      // 图片剩余名额：已选图片数 + 本次新增不能超过上限
      let remainingImageCount = MAX_IMAGE_COUNT - this.attachments.filter(a => a.kind === 'image').length;
      let warnedImageLimit = false;
      let warnedSizeLimit = false;
      for (const file of Array.from(files)) {
        const kind = kindOf(file.name);
        // 图片数量上限：超出部分不传，仅提示一次（避免逐张弹窗）
        if (kind === 'image' && remainingImageCount <= 0) {
          if (!warnedImageLimit) {
            this.messageSrv.warning(`最多发送 ${MAX_IMAGE_COUNT} 张图片`);
            warnedImageLimit = true;
          }
          continue;
        }
        // 图片尺寸上限：单张任一边超过 5000px 拒绝上传，仅提示一次
        if (kind === 'image') {
          const size = await this.getImageSize(file);
          if (size && (size.width > MAX_IMAGE_PIXEL || size.height > MAX_IMAGE_PIXEL)) {
            if (!warnedSizeLimit) {
              this.messageSrv.warning(`单张图片不能超过 ${MAX_IMAGE_PIXEL}×${MAX_IMAGE_PIXEL} 像素`);
              warnedSizeLimit = true;
            }
            continue;
          }
        }
        try {
          const path = await this.cos.upload(file, file.name);
          this.attachments = [
            ...this.attachments,
            {
              url: this.cos.toUrl(path),
              name: file.name,
              kind,
            },
          ];
          if (kind === 'image') remainingImageCount--;
          // OnPush：上传 await 续体非模板事件，已传附件/可发送态需主动标记
          this.cdr.markForCheck();
        } catch {
          this.messageSrv.error(`上传「${file.name}」失败`);
        }
      }
    } finally {
      this.uploading = false;
    }
    // OnPush：整批上传结束（await 续体）非模板事件，上传态需主动标记
    this.cdr.markForCheck();
  }

  /** 删除已选附件 */
  onRemoveAttachment(index: number) {
    this.attachments = this.attachments.filter((_, i) => i !== index);
  }

  /** 输入时自适应高度：默认单行，内容换行自动撑高；空内容时回到单行（避免长占位文案把高度撑成多行） */
  autoGrow() {
    const el = this.composerInput()?.nativeElement;
    if (!el) return;
    const hasBreak = this.content.includes('\n');
    if (!this.content) {
      el.style.height = '';
      this.isWrapped = false;
      return;
    }
    el.style.height = 'auto';
    // 折行检测：内容真实高度超过"单行高"（行高 + 上下 padding）即视为已折行。
    // 单行总高 = lineHeight(14×1.6≈22.4) + paddingTop/Bottom(各8px)；用 getComputedStyle 取值避免写死
    const cs = getComputedStyle(el);
    const lineHeight = parseFloat(cs.lineHeight) || 22.4;
    const singleLineHeight = lineHeight + (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
    this.isWrapped = hasBreak || el.scrollHeight > singleLineHeight + 1;
    el.style.height = `${el.scrollHeight}px`;
  }

  /** 空内容且无附件、或上传中，不发送 */
  onSubmit() {
    if (this.uploading) return;
    if (!this.content.trim() && this.attachments.length === 0) return;
    this.send.emit({
      content: this.content.trim(),
      mediaUrls: this.attachments.map(a => a.url),
    });
  }

  /** 点击右上角 × 关闭回复器 */
  onCancel() {
    this.cancel.emit();
  }
}
